import { FC, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Citation, Message } from '@/types/agents';
import agentsService from '@/services/agents.service';
import ModelBadge from '@/components/assistant/ModelBadge';
import { ModelCatalog } from '@/utils/modelIdentity';

interface ChatMessageProps {
  message: Message;
  /** Ícone/emoji do agente, exibido ao lado das respostas do assistant. */
  avatar?: string;
  /** Catálogo pra resolver `model_used` em nome+fornecedor. */
  modelCatalog?: ModelCatalog | null;
}

/** Dedupe por arquivo+página preservando a ordem (chunks repetem fonte). */
function dedupeCitations(citations: Citation[]): Citation[] {
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const c of citations) {
    const key = `${c.file_id ?? c.source_file}:${c.page_number ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

const ChatMessage: FC<ChatMessageProps> = ({ message, avatar, modelCatalog }) => {
  const [showSql, setShowSql] = useState(false);
  const isUser = message.role === 'USER';
  const sql = typeof message.metadata?.sql === 'string' ? message.metadata.sql : '';
  const iterations = Array.isArray(message.metadata?.iterations) ? message.metadata.iterations : [];
  const citations =
    !isUser && Array.isArray(message.metadata?.citations) ? dedupeCitations(message.metadata.citations as Citation[]) : [];

  async function openSource(c: Citation) {
    if (!c.file_id) return;
    try {
      const url = await agentsService.getFileUrl(c.file_id);
      if (!url) return;
      const target = c.page_number ? `${url}#page=${c.page_number}` : url;
      window.open(target, '_blank', 'noopener');
    } catch {
      /* sem URL — ignora (chip vira no-op) */
    }
  }

  return (
    <div
      data-testid="chat-message"
      data-role={message.role}
      className={`flex items-end gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <div className="shrink-0 w-8 h-8 rounded-full bg-surface-tertiary border border-surface-border grid place-items-center text-accent">
          <span className="material-symbols-outlined text-lg leading-none">{avatar || 'psychology'}</span>
        </div>
      )}
      <div
        className={`max-w-[85%] md:max-w-3xl rounded-2xl px-5 py-3 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.35)] ${
          isUser
            ? 'bg-accent/15 border border-accent/30 text-content-primary rounded-br-sm'
            : 'bg-surface-secondary border border-surface-border text-content-primary rounded-bl-sm'
        }`}
      >
        <div
          className="prose prose-lg prose-tema max-w-none
            prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2
            prose-ul:my-2 prose-ol:my-2 prose-li:my-1
            prose-table:my-3 prose-th:px-2 prose-td:px-2
            prose-strong:text-content-primary
            prose-code:text-accent prose-code:before:content-none prose-code:after:content-none
            prose-a:text-accent"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </div>

        {!isUser && sql && (
          <div className="mt-3 pt-3 border-t border-surface-border">
            <button
              type="button"
              data-testid="toggle-sql"
              onClick={() => setShowSql((v) => !v)}
              className="text-xs text-content-tertiary hover:text-accent flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-sm">code</span>
              {showSql ? 'Ocultar SQL' : 'Ver SQL executado'}
              {iterations.length > 1 && (
                <span className="text-2xs text-amber-400 ml-1">({iterations.length} iterações)</span>
              )}
            </button>
            {showSql && (
              <pre
                data-testid="sql-block"
                className="mt-2 text-xs bg-black/40 rounded-lg p-3 overflow-x-auto text-content-secondary"
              >
                <code>{sql}</code>
              </pre>
            )}
          </div>
        )}

        {!isUser && citations.length > 0 && (
          <div data-testid="citations" className="mt-3 pt-3 border-t border-surface-border">
            <p className="text-2xs uppercase tracking-wider text-content-tertiary mb-1.5 font-semibold">Fontes</p>
            <div className="flex flex-wrap gap-1.5">
              {citations.map((c, i) => (
                <button
                  key={`${c.file_id ?? c.source_file}:${c.page_number ?? ''}:${i}`}
                  type="button"
                  data-testid="citation-chip"
                  onClick={() => openSource(c)}
                  disabled={!c.file_id}
                  title={c.snippet || c.source_file}
                  className="text-2xs px-2 py-1 rounded-full bg-surface-tertiary border border-surface-border text-content-secondary hover:border-accent hover:text-accent transition inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-default"
                >
                  <span className="material-symbols-outlined text-[13px] leading-none">description</span>
                  {c.source_file}
                  {c.page_number ? ` · p.${c.page_number}` : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fecha o ciclo do Hub: quem criou o agente escolheu o modelo, e vê esse mesmo
            nome de volta na resposta. */}
        {!isUser && message.model_used && (
          <div className="mt-2 pt-2 border-t border-surface-border/60">
            <ModelBadge
              info={message.model_info}
              model={message.model_used}
              catalog={modelCatalog}
              variant="inline"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
